// Tabulka seznamu: sloupce podle kontraktu, řazení, výběr (klik/Ctrl/Shift/Ctrl+A), kontextové menu,
// změna šířky a skrytí sloupců (uloženo per uživatel), klávesnice. Data dává okno (stránkování na serveru).
import { h, clear, clamp } from '../dom';
import { fmtDate, fmtDateTime, fmtNumber, tk } from '../i18n';
import { icon } from '../icons';
import type { ColumnDef } from '../contract';
import { IwMenu, type MenuItem } from './menu';
import { isPh, vwindow, navTarget, rangeIds, type VWindow } from './virtual';

/** Typ dat přetahovaných řádků seznamu (tabulka i dlaždice): JSON pole id. */
export const ROWS_MIME = 'application/x-iwan-rows';

/** Naplní dataTransfer přetahovanými řádky; stejně to dělá tabulka i dlaždice. */
export function setRowsDrag(e: DragEvent, ids: string[], label: string): void {
  if (!e.dataTransfer) return;
  e.dataTransfer.setData(ROWS_MIME, JSON.stringify(ids));
  e.dataTransfer.setData('text/plain', ids.length > 1 ? String(ids.length) : label);
  e.dataTransfer.effectAllowed = 'move';
}

/** Id řádků z přetažení (null = přetahuje se něco jiného, např. soubor z počítače). */
export function rowsFromDrag(e: DragEvent): string[] | null {
  if (!e.dataTransfer?.types.includes(ROWS_MIME)) return null;
  try {
    const v = JSON.parse(e.dataTransfer.getData(ROWS_MIME)) as unknown;
    return Array.isArray(v) ? v.map(String) : null;
  } catch {
    return null;
  }
}

export interface ColumnState {
  width?: number;
  hidden?: boolean;
  order?: number;
}

export type Row = Record<string, unknown>;

export class IwTable extends HTMLElement {
  private columns: ColumnDef[] = [];
  private state: Record<string, ColumnState> = {};
  private rows: Row[] = [];
  private rowId = 'id';
  private selected = new Set<string>();
  private anchor: string | null = null;
  private cursor: string | null = null;
  private sort: { col: string; dir: 'asc' | 'desc' } | null = null;
  private table = h('table', { class: 't' });
  private thead = h('thead');
  private tbody = h('tbody');
  private emptyEl = h('div', { class: 'table-empty' });
  private scrollEl = h('div', { class: 'table-scroll' });
  /** Plynulý seznam: vykreslí se jen okno řádků kolem posunu, o nenačtené si řekne přes onRange. */
  virtual = false;
  onRange: ((from: number, to: number) => void) | null = null;
  /** Shift-výběr přes nenačtené: dočíst řádky from..to (pohled) */
  loadRange: ((from: number, to: number) => Promise<void>) | null = null;
  private lineH = 0;
  private measuring = false;
  /** kurzor míří na ještě nenačtený řádek – vybere se, až dorazí (updateRows) */
  private pending: { i: number; extend: boolean } | null = null;
  private win: VWindow = { start: -1, end: -1, padTop: 0, padBottom: 0 };
  private raf = 0;
  onSort: ((sort: { col: string; dir: 'asc' | 'desc' }) => void) | null = null;
  onSelect: ((ids: string[]) => void) | null = null;
  onOpen: ((id: string, row: Row) => void) | null = null;
  onContext: ((ids: string[], e: MouseEvent | PointerEvent) => MenuItem[] | null) | null = null;
  onColumnState: ((state: Record<string, ColumnState>) => void) | null = null;
  emptyText = '';
  /** Režim výběru (dotyk): zaškrtávátko v prvním sloupci, klepnutí přepíná výběr místo otevření. */
  selectMode = false;
  /** Řádky jde přetáhnout (ROWS_MIME = JSON seznam id výběru); cíl si určuje pohled (např. vrstva v panelu). */
  rowsDraggable = false;

  setSelectMode(on: boolean): void {
    this.selectMode = on;
    this.classList.toggle('select-mode', on);
    this.renderBody();
  }

  connectedCallback(): void {
    if (!this.hasChildNodes()) {
      this.table.append(this.thead, this.tbody);
      this.scrollEl.appendChild(this.table);
      this.append(this.scrollEl, this.emptyEl);
      this.scrollEl.addEventListener('scroll', () => { if (this.virtual) { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(() => this.renderWindow(false)); } }, { passive: true });
      new ResizeObserver(() => { if (this.virtual) this.renderWindow(true); }).observe(this.scrollEl);
      this.tabIndex = 0;
      this.setAttribute('role', 'grid');
      this.addEventListener('keydown', (e) => this.onKey(e));
      this.addEventListener('contextmenu', (e) => this.onContextMenu(e));
      this.addEventListener('pointerdown', (e) => { this.lastPointer = e.pointerType; }, true);
    }
  }
  /** Dlouhý stisk prstem: místo kontextové nabídky režim výběru (telefon, jako dlaždice). */
  onLongPress: ((id: string) => void) | null = null;
  /** Klepnutí (mimo režim výběru) záznam rovnou otevře – telefon. */
  tapOpens = false;
  private suppressUntil = 0;
  private lastPointer = '';

  setColumns(columns: ColumnDef[], state: Record<string, ColumnState> = {}, sort: { col: string; dir: 'asc' | 'desc' } | null = null): void {
    this.columns = columns;
    this.state = state;
    this.sort = sort;
    this.renderHead();
  }

  setSort(sort: { col: string; dir: 'asc' | 'desc' } | null): void {
    this.sort = sort;
    this.renderHead();
  }

  setRows(rows: Row[], rowId: string): void {
    this.rows = rows;
    this.rowId = rowId;
    if (!this.virtual) {
      const ids = new Set(rows.map((r) => String(r[rowId])));
      for (const id of Array.from(this.selected)) if (!ids.has(id)) this.selected.delete(id);
    }
    this.classList.toggle('virtual', this.virtual);
    this.renderBody();
    this.onSelect?.(this.selection());
  }

  /** Plynulý seznam: dočtené řádky (stejná délka) – bez ztráty posunu a výběru. */
  updateRows(rows: Row[]): void {
    this.rows = rows;
    this.renderWindow(true);
    const p = this.pending;
    if (p && !isPh(this.rows[p.i])) this.moveTo(p.i, p.extend);
  }

  /** Nahradí výběr (vrácení výběru na záznam v detailu, prořezání po obnovení). */
  select(ids: string[]): void {
    const same = ids.length === this.selected.size && ids.every((id) => this.selected.has(id));
    if (same) return;
    this.selected = new Set(ids);
    if (this.anchor && !this.selected.has(this.anchor)) this.anchor = ids[0] ?? null;
    this.paintSelection();
    this.onSelect?.(this.selection());
  }

  selectIds(ids: string[]): void {
    for (const id of ids) this.selected.add(id);
    this.paintSelection();
    this.onSelect?.(this.selection());
  }

  scrollToIndex(i: number): void {
    this.scrollEl.scrollTop = i * (this.lineH || 34);
    this.renderWindow(true);
  }

  selection(): string[] {
    if (this.virtual) return Array.from(this.selected);
    return this.rows.map((r) => String(r[this.rowId])).filter((id) => this.selected.has(id));
  }

  selectedRows(): Row[] {
    return this.rows.filter((r) => this.selected.has(String(r[this.rowId])));
  }

  selectAll(): void {
    for (const r of this.rows) if (!isPh(r)) this.selected.add(String(r[this.rowId]));
    this.paintSelection();
    this.onSelect?.(this.selection());
  }

  clearSelection(): void {
    this.selected.clear();
    this.paintSelection();
    this.onSelect?.([]);
  }

  visibleColumns(): ColumnDef[] {
    return this.columns
      .filter((c) => !(this.state[c.key]?.hidden ?? c.hidden))
      .sort((a, b) => (this.state[a.key]?.order ?? 0) - (this.state[b.key]?.order ?? 0));
  }

  columnMenu(): MenuItem[] {
    return this.columns.map((c) => ({
      label: tk(c.label),
      checked: !(this.state[c.key]?.hidden ?? c.hidden),
      onSelect: () => {
        const cur = this.state[c.key]?.hidden ?? c.hidden;
        this.state[c.key] = { ...this.state[c.key], hidden: !cur };
        this.renderHead();
        this.renderBody();
        this.onColumnState?.(this.state);
      },
    }));
  }

  private renderHead(): void {
    clear(this.thead);
    const tr = h('tr');
    for (const c of this.visibleColumns()) {
      const width = this.state[c.key]?.width ?? c.width;
      const th = h(
        'th',
        { class: (c.sortable ? 'sortable' : '') + (c.align ? ' ' + c.align : ''), style: width ? { width: width + 'px', minWidth: width + 'px' } : null, dataset: { key: c.key }, scope: 'col' },
        h('span', { class: 'th-label' }, tk(c.label)),
        this.sort?.col === c.key ? icon(this.sort.dir === 'asc' ? 'arrow-up' : 'chevron-down', 'icon sort-icon') : null,
        h('span', { class: 'th-resize' }),
      );
      if (c.sortable) {
        th.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).classList.contains('th-resize')) return;
          const dir = this.sort?.col === c.key && this.sort.dir === 'asc' ? 'desc' : 'asc';
          this.sort = { col: c.key, dir };
          this.renderHead();
          this.onSort?.(this.sort);
        });
      }
      th.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        IwMenu.show(this.columnMenu(), e.clientX, e.clientY);
      });
      this.resizable(th, c.key);
      tr.appendChild(th);
    }
    this.thead.appendChild(tr);
  }

  private resizable(th: HTMLElement, key: string): void {
    const handle = th.querySelector<HTMLElement>('.th-resize');
    if (!handle) return;
    handle.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = th.getBoundingClientRect().width;
      handle.setPointerCapture(e.pointerId);
      const move = (ev: PointerEvent) => {
        const w = clamp(startW + ev.clientX - startX, 40, 900);
        th.style.width = w + 'px';
        th.style.minWidth = w + 'px';
      };
      const up = () => {
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', up);
        this.state[key] = { ...this.state[key], width: Math.round(th.getBoundingClientRect().width) };
        this.onColumnState?.(this.state);
      };
      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', up);
    });
  }

  private renderBody(): void {
    if (this.virtual) {
      this.renderWindow(true);
      return;
    }
    clear(this.tbody);
    const cols = this.visibleColumns();
    this.emptyEl.textContent = this.rows.length ? '' : this.emptyText;
    this.emptyEl.hidden = this.rows.length > 0;
    for (const row of this.rows) this.tbody.appendChild(this.rowEl(row, cols));
  }

  private renderWindow(force: boolean): void {
    const cols = this.visibleColumns();
    this.emptyEl.textContent = this.rows.length ? '' : this.emptyText;
    this.emptyEl.hidden = this.rows.length > 0;
    const lineH = this.lineH || 34;
    const head = this.thead.getBoundingClientRect().height;
    const w = vwindow(this.rows.length, 1, lineH, Math.max(0, this.scrollEl.scrollTop - head), this.scrollEl.clientHeight || 600);
    if (!force && w.start === this.win.start && w.end === this.win.end) return;
    this.win = w;
    clear(this.tbody);
    const pad = (px: number) => h('tr', { class: 'v-pad', 'aria-hidden': 'true' }, h('td', { colspan: String(Math.max(1, cols.length)), style: { height: px + 'px' } }));
    if (w.padTop) this.tbody.appendChild(pad(w.padTop));
    for (let i = w.start; i < w.end; i++) this.tbody.appendChild(this.rowEl(this.rows[i] as Row, cols));
    if (w.padBottom) this.tbody.appendChild(pad(w.padBottom));
    const real = this.tbody.querySelector<HTMLElement>('tr[data-id]');
    if (!this.measuring && real && real.offsetHeight > 10 && Math.abs(real.offsetHeight - lineH) > 1) {
      this.lineH = real.offsetHeight; // přeměřit nejvýš jednou za vykreslení
      this.measuring = true;
      try {
        this.renderWindow(true);
      } finally {
        this.measuring = false;
      }
      return;
    }
    this.onRange?.(w.start, w.end);
  }

  private rowEl(row: Row, cols: ColumnDef[]): HTMLElement {
    if (isPh(row)) return h('tr', { class: 'ph', 'aria-hidden': 'true' }, h('td', { colspan: String(Math.max(1, cols.length)) }, h('div', { class: 'ph-bar' })));
    {
      const id = String(row[this.rowId]);
      const tr = h('tr', { dataset: { id }, 'aria-selected': this.selected.has(id) ? 'true' : 'false', class: this.selected.has(id) ? 'selected' : '' });
      cols.forEach((c, i) => {
        const td = this.cell(c, row);
        if (i === 0 && this.selectMode) td.prepend(h('span', { class: 'sel-check', 'aria-hidden': 'true' }, icon('check')));
        tr.appendChild(td);
      });
      tr.addEventListener('click', (e) => this.onRowClick(e, id));
      tr.addEventListener('dblclick', () => this.onOpen?.(id, row));
      if (this.rowsDraggable) {
        tr.setAttribute('draggable', 'true');
        tr.addEventListener('dragstart', (e) => {
          if (!this.selected.has(id)) {
            this.selected.clear();
            this.selected.add(id);
            this.anchor = id;
            this.cursor = id;
            this.paintSelection();
            this.onSelect?.(this.selection());
          }
          setRowsDrag(e, this.selection(), String(row[this.rowId] ?? ''));
        });
      }
      return tr;
    }
  }

  /** Text buňky jako v tabulce (datum, číslo, výčet, ano) – pro automatickou vizitku řádku. */
  static cellText(c: ColumnDef, row: Row): string {
    const v = row[c.key];
    if (v === null || v === undefined || v === '') return '';
    switch (c.type) {
      case 'bool': return v === true || v === 1 || v === '1' ? '✓ ' + tk(c.label) : '';
      case 'number': return fmtNumber(v);
      case 'date': return fmtDate(v);
      case 'datetime': return fmtDateTime(v);
      case 'enum': { const opt = c.options?.find((o) => String(o.value) === String(v)); return opt ? tk(opt.label) : String(v); }
      case 'image': return '';
      default: return String(v);
    }
  }

  private cell(c: ColumnDef, row: Row): HTMLElement {
    const v = row[c.key];
    const td = h('td', { class: (c.align ?? '') + (c.mono ? ' mono' : ''), dataset: { key: c.key } });
    switch (c.type) {
      case 'bool':
        if (v === true || v === 1 || v === '1') td.appendChild(icon('check', 'icon cell-check'));
        break;
      case 'number':
        td.textContent = fmtNumber(v);
        break;
      case 'date':
        td.textContent = fmtDate(v);
        break;
      case 'datetime':
        td.textContent = fmtDateTime(v);
        break;
      case 'enum': {
        const opt = c.options?.find((o) => String(o.value) === String(v));
        if (opt) {
          // barva: tón (ok | warn | bad | error | info) jako třída, jinak barva CSS (#hex)
          const tone = opt.color && /^(ok|warn|bad|error|info)$/.test(opt.color) ? opt.color : '';
          const css = opt.color && !tone ? opt.color : null;
          const style = css ? { color: css, background: `color-mix(in srgb, ${css} 14%, transparent)` } : null;
          td.appendChild(h('span', { class: 'badge ' + tone, style }, c.dot ? h('span', { class: 'badge-dot' }) : null, tk(opt.label)));
        }
        else td.textContent = v === null || v === undefined ? '' : String(v);
        break;
      }
      case 'image':
        if (typeof v === 'string' && v) td.appendChild(h('img', { class: 'cell-thumb', src: v, alt: '', loading: 'lazy' }));
        break;
      default:
        td.textContent = v === null || v === undefined ? '' : String(v);
    }
    const hint = c.hint ? row[c.hint] : null;
    if (typeof hint === 'string' && hint !== '') td.title = hint;
    if (!td.title && (td.textContent ?? '').length > 24) td.title = td.textContent ?? ''; // celá hodnota v bublině (buňka má výpustku)
    return td;
  }

  private onRowClick(e: MouseEvent, id: string): void {
    if (Date.now() < this.suppressUntil) return;
    if (this.tapOpens && !this.selectMode && !e.ctrlKey && !e.metaKey && !e.shiftKey) { // telefon: klepnutí otevře (jako dlaždice)
      const row = this.rows.find((r) => String(r[this.rowId]) === id);
      if (!row) return;
      this.selected.clear();
      this.selected.add(id);
      this.anchor = id;
      this.onSelect?.(this.selection());
      this.onOpen?.(id, row);
      window.setTimeout(() => this.clearSelection(), 0);
      return;
    }
    if (this.selectMode && !e.shiftKey) {
      if (this.selected.has(id)) this.selected.delete(id);
      else this.selected.add(id);
      this.anchor = id;
      this.cursor = id;
      this.paintSelection();
      this.onSelect?.(this.selection());
      return;
    }
    if (e.shiftKey && this.anchor) {
      this.cursor = id;
      void this.selectRange(this.indexOf(this.anchor), this.indexOf(id), e.ctrlKey);
      return;
    } else if (e.ctrlKey || e.metaKey) {
      if (this.selected.has(id)) this.selected.delete(id);
      else this.selected.add(id);
      this.anchor = id;
    } else {
      this.selected.clear();
      this.selected.add(id);
      this.anchor = id;
    }
    this.cursor = id;
    this.paintSelection();
    this.onSelect?.(this.selection());
  }

  private paintSelection(): void {
    this.tbody.querySelectorAll<HTMLTableRowElement>('tr').forEach((tr) => {
      const sel = this.selected.has(tr.dataset['id'] ?? '');
      tr.classList.toggle('selected', sel);
      tr.setAttribute('aria-selected', sel ? 'true' : 'false');
      tr.classList.toggle('cursor', tr.dataset['id'] === this.cursor);
    });
  }

  private indexOf(id: string): number {
    return this.rows.findIndex((r) => String(r[this.rowId]) === id);
  }

  /** Rozsah od kotvy (Shift) podle pozice – i přes nenačtené řádky; add = přidat k výběru (Ctrl+Shift). */
  private async selectRange(a: number, b: number, add = false): Promise<void> {
    if (a < 0 || b < 0) return;
    const ids = await rangeIds(() => this.rows, this.rowId, a, b, this.loadRange);
    if (!add) this.selected.clear();
    for (const id of ids) this.selected.add(id);
    this.paintSelection();
    this.onSelect?.(this.selection());
  }

  /** Kurzor klávesnice na řádek i: vybrat ho (Shift = rozšířit od kotvy), posunout do zorného pole. */
  private moveTo(i: number, extend: boolean): void {
    const lineH = this.lineH || 34;
    const head = this.thead.getBoundingClientRect().height;
    const top = i * lineH;
    const s = this.scrollEl;
    if (this.virtual && top < s.scrollTop) s.scrollTop = top;
    else if (this.virtual && top + lineH + head > s.scrollTop + s.clientHeight) s.scrollTop = top + lineH + head - s.clientHeight;
    const row = this.rows[i];
    if (isPh(row)) {
      this.pending = { i, extend }; // dočte se přes onRange, vybere se v updateRows
      if (this.virtual) this.renderWindow(false);
      return;
    }
    this.pending = null;
    const id = String((row as Row)[this.rowId]);
    this.cursor = id;
    if (extend && this.anchor) {
      void this.selectRange(this.indexOf(this.anchor), i);
      return;
    }
    this.selected.clear();
    this.selected.add(id);
    this.anchor = id;
    this.paintSelection();
    if (!this.virtual) this.tbody.querySelector(`tr[data-id="${CSS.escape(id)}"]`)?.scrollIntoView({ block: 'nearest' });
    this.onSelect?.(this.selection());
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.rows.length) return;
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      this.selectAll();
      return;
    }
    const lineH = this.lineH || 34;
    const next = navTarget(e.key, this.cursor ? this.indexOf(this.cursor) : -1, this.rows.length, 1, Math.max(1, Math.floor(this.scrollEl.clientHeight / lineH) - 2));
    if (next !== null && !e.altKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      this.moveTo(next, e.shiftKey);
      return;
    }
    if (e.key === 'Enter' && this.cursor) {
      const row = this.rows.find((r) => String(r[this.rowId]) === this.cursor);
      if (row) {
        e.preventDefault();
        this.onOpen?.(this.cursor, row);
      }
      return;
    }
    if (e.key === 'Escape' && !this.virtual) { // plynulý seznam: Esc řeší pohled (rozpracovaný detail má přednost)
      this.clearSelection();
    }
  }

  private onContextMenu(e: MouseEvent): void {
    const tr = (e.target as HTMLElement).closest('tr[data-id]') as HTMLElement | null;
    if (!tr) return;
    const id = tr.dataset['id'] ?? '';
    if (this.onLongPress && (this.lastPointer === 'touch' || Date.now() < this.suppressUntil)) { // dlouhý stisk prstem = režim výběru
      e.preventDefault();
      e.stopPropagation();
      if (Date.now() >= this.suppressUntil) {
        this.suppressUntil = Date.now() + 1200; // prohlížeč po stisku pošle ještě click
        if (!this.selected.has(id)) { this.selected.clear(); this.selected.add(id); this.anchor = id; this.cursor = id; this.paintSelection(); this.onSelect?.(this.selection()); }
        this.onLongPress(id);
      }
      return;
    }
    if (!this.selected.has(id)) {
      this.selected.clear();
      this.selected.add(id);
      this.anchor = id;
      this.cursor = id;
      this.paintSelection();
      this.onSelect?.(this.selection());
    }
    const items = this.onContext?.(this.selection(), e);
    if (!items) return;
    e.preventDefault();
    IwMenu.show(items, e.clientX, e.clientY);
  }
}

customElements.define('iw-table', IwTable);
